import { useEffect, useState } from 'react';
import { generate, getRgbStr } from '@arco-design/color';
import { ApiGetPublicBranding } from '@/api';
/**
 * 白标品牌 (按访问的域名加载)
 *
 * 后端 /api/team/branding/public 依据 window.location.hostname 返回该域名的品牌
 * (brand_name / logo / logo_dark / favicon / primary_color / title / description);
 * 域名未配置品牌(或不是自定义域名)时 found=false, 保持默认品牌。
 *
 * 结果会缓存到 localStorage: 同源二次访问可在 React 渲染前同步套用, 避免闪烁。
 * localStorage 按源隔离, 但缓存里仍记录 hostname 做二次校验。
 */

const STORAGE_KEY = 'portal-branding';
/** 主色是一套 1..10 的色阶 (Arco 的 --arcoblue-N / --primary-N) */

const PRIMARY_LEVELS = 10;
let branding = null;
let initialized = false;
let defaultTitle = null;
let defaultFaviconHref = null;
const listeners = new Set();

function notify() {
  listeners.forEach(listener => listener());
}
/** 记录页面原始标题/图标, 用于品牌被移除时回退 */


function ensureDefaults() {
  if (defaultTitle === null) {
    defaultTitle = document.title;
  }

  if (defaultFaviconHref === null) {
    const link = document.querySelector('link[rel~="icon"]');
    defaultFaviconHref = link ? link.getAttribute('href') || '' : '';
  }
}

function readCache() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const cached = JSON.parse(raw);

    if (!cached || cached.hostname !== window.location.hostname) {
      return null;
    }

    return cached.branding || null;
  } catch {
    // 缓存损坏 / localStorage 不可用(隐私模式)时忽略
    return null;
  }
}

function writeCache(value) {
  try {
    if (value) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        hostname: window.location.hostname,
        branding: value
      }));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {// 写缓存失败不影响本次渲染
  }
}

function applyFavicon(href) {
  let link = document.querySelector('link[rel~="icon"]');

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.setAttribute('href', href || defaultFaviconHref || '');
}
/**
 * 覆盖 Arco 主色阶: --primary-N 是 var(--arcoblue-N) 的别名,
 * 因此改 --arcoblue-N 即可影响按钮/链接等所有主色元素 (与 Settings 取色器一致)。
 */


function applyPrimaryColor(color) {
  const levels = color ? generate(color, {
    list: true
  }) : null;

  for (let i = 1; i <= PRIMARY_LEVELS; i += 1) {
    if (levels && levels[i - 1]) {
      document.body.style.setProperty(`--arcoblue-${i}`, getRgbStr(levels[i - 1]));
    } else {
      document.body.style.removeProperty(`--arcoblue-${i}`);
    }
  }
}
/** 套用品牌(传 null 表示恢复默认), 并通知订阅者 */


export function applyBranding(value) {
  ensureDefaults();
  branding = value;
  document.title = value && (value.title || value.brand_name) || defaultTitle || '';
  applyFavicon(value ? value.favicon : '');
  applyPrimaryColor(value ? value.primary_color : '');
  notify();
}
export function getBranding() {
  return branding;
}
export function subscribeBranding(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
/** 深色模式优先 logo_dark, 为空回退 logo; 无品牌时返回空串(由调用方回退默认 Logo) */

export function brandingLogo(value, theme) {
  if (!value) {
    return '';
  }

  return theme === 'dark' ? value.logo_dark || value.logo : value.logo;
}
/**
 * 首屏同步套用缓存品牌: 在 ReactDOM.render 之前调用, 避免默认品牌一闪而过。
 * 每个页面只生效一次。
 */

export function initBrandingFromCache() {
  if (initialized) {
    return;
  }

  initialized = true;
  const cached = readCache();

  if (cached) {
    applyBranding(cached);
  }
}
/** 按当前访问域名拉取品牌; 失败时保留缓存/默认品牌, 不打断页面 */

export async function loadBranding() {
  const {
    hostname
  } = window.location;

  if (!hostname) {
    return branding;
  }

  try {
    // 公开接口对未知域名返回 code 0 + found=false, 不经过错误分支
    const res = await ApiGetPublicBranding({
      hostname
    });
    const data = res?.data?.data;
    const value = data && data.found ? data.branding : null;
    writeCache(value);
    applyBranding(value);
    return value;
  } catch {
    return branding;
  }
}
/** 组件里读取品牌 (随品牌加载/更新自动重渲染) */

export function useBranding() {
  const [value, setValue] = useState(() => getBranding());
  useEffect(() => subscribeBranding(() => setValue(getBranding())), []);
  return value;
}